import { LogOut, UserRoundCog } from 'lucide-react'
import { useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { selectImpersonation } from '@/features/auth/impersonation'
import { useExitImpersonation } from '@/features/auth/useExitImpersonation'
import {
  IMPERSONATION_EXIT,
  IMPERSONATION_EXITING,
  IMPERSONATION_REGION_LABEL,
  exitLabel,
  viewingAsText,
} from '@/features/auth/impersonationCopy'
import { assistAnchor } from '@/features/assist/anchors'

/**
 * @param {Object} props
 * @param {() => void} props.onExit
 * @param {boolean} props.isExiting
 * @param {string} props.label - accessible name
 * @param {string} [props.className]
 */
function ExitButton({ onExit, isExiting, label, className }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onExit}
      disabled={isExiting}
      aria-busy={isExiting || undefined}
      aria-label={label}
      className={cn('shrink-0', className)}
      {...assistAnchor('impersonation.exit')}
    >
      <LogOut aria-hidden="true" />
      {isExiting ? IMPERSONATION_EXITING : IMPERSONATION_EXIT}
    </Button>
  )
}

/**
 * The always-visible "Viewing as {target} · {tenant}" block while a superuser
 * impersonates someone (ClickUp 86e39gctg). Not dismissable: it renders for
 * as long as `/auth/me` reports the impersonation, and nothing otherwise.
 *
 * - `sidebar`: top of the expanded desktop sidebar, with Exit.
 * - `rail`: the collapsed rail's badge plus a persistent Exit icon button -
 *   not hover-only, so keyboard and touch users can reach it.
 * - `bar`: phone width, where the sidebar is hidden - a strip atop the page.
 *
 * @param {Object} props
 * @param {'sidebar'|'rail'|'bar'} [props.variant='sidebar']
 * @returns {JSX.Element|null}
 */
export default function ImpersonationBanner({ variant = 'sidebar' }) {
  const impersonation = useSelector(selectImpersonation)
  const { exit, isExiting } = useExitImpersonation()

  if (!impersonation?.active) return null

  const text = viewingAsText(impersonation)

  if (variant === 'rail') {
    const label = exitLabel(impersonation)
    return (
      <div className="flex flex-col items-center gap-2 px-2 pt-3" data-testid="impersonation-banner">
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              role="status"
              tabIndex={0}
              aria-label={text}
              className="flex size-10 items-center justify-center rounded-full bg-warning-subtle text-warning-ink ring-2 ring-warning-solid focus-visible:outline-none focus-visible:ring-navigation-ink"
            >
              <UserRoundCog className="size-5" aria-hidden="true" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{text}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={exit}
              disabled={isExiting}
              aria-busy={isExiting || undefined}
              aria-label={label}
              {...assistAnchor('impersonation.exit')}
            >
              <LogOut aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{isExiting ? IMPERSONATION_EXITING : label}</TooltipContent>
        </Tooltip>
      </div>
    )
  }

  if (variant === 'bar') {
    return (
      <div
        role="region"
        aria-label={IMPERSONATION_REGION_LABEL}
        data-testid="impersonation-banner"
        className="flex shrink-0 items-center gap-2 border-b border-warning-solid/40 bg-warning-subtle px-4 py-1.5 text-warning-ink md:hidden"
      >
        <UserRoundCog className="size-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium">{text}</p>
        <ExitButton onExit={exit} isExiting={isExiting} label={exitLabel(impersonation)} />
      </div>
    )
  }

  return (
    <div className="px-2 pt-3" data-testid="impersonation-banner">
      <div
        role="region"
        aria-label={IMPERSONATION_REGION_LABEL}
        className="flex flex-col gap-2 rounded-control border border-warning-solid/40 bg-warning-subtle p-3 text-warning-ink"
      >
        <div className="flex min-w-0 items-start gap-2">
          <UserRoundCog className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm font-medium break-words">{text}</p>
        </div>
        <ExitButton onExit={exit} isExiting={isExiting} label={exitLabel(impersonation)} className="w-full" />
      </div>
    </div>
  )
}
